import { useEffect, useRef, useState } from 'react'
import { Button, Checkbox, Input, Select } from 'antd'
import { OrderLayout } from './OrderLayout'
import { TelegramGift } from './TelegramGift'
import { ElectricProductImage } from './ElectricProductImage'
import { WbDtuHelp } from './WbDtuHelp'
import { IssueLoader } from './IssueLoader'
import BG from '../assets/bg.jpg'
import './WbDtuPage.css'

export function WbDtuPage({ token }) {
  const [purchase, setPurchase] = useState(null), [values, setValues] = useState({})
  const [confirmed, setConfirmed] = useState(false), [error, setError] = useState(''), [sending, setSending] = useState(false)
  const mounted = useRef(false), lock = useRef(false)
  const valid = /^[a-f0-9]{64}$/.test(token || '')
  useEffect(() => {
    mounted.current = true
    if (!valid) { setError('Ссылка не найдена. Откройте персональную ссылку из чата заказа WB.'); return () => { mounted.current = false } }
    let stopped = false, initialized = false, timer, controller
    const poll = async () => {
      controller = new AbortController()
      try {
        const response = await fetch('/api/wb-dtu/' + token, { signal: controller.signal, cache: 'no-store' })
        const body = await response.json()
        if (!response.ok) throw new Error(body.error || 'Не удалось загрузить заказ')
        if (!stopped) {
          if (!initialized) { setValues(body.purchase.values || {}); initialized = true }
          setPurchase(previous => previous && previous.revision > body.purchase.revision ? previous : body.purchase)
          if (!body.purchase.canSubmit) setError('')
        }
      } catch (e) { if (!stopped) setError(e.message) }
      finally { if (!stopped) timer = setTimeout(poll, 5000) }
    }
    poll()
    return () => { stopped = true; mounted.current = false; clearTimeout(timer); controller?.abort() }
  }, [token, valid])
  const submit = async event => {
    event.preventDefault()
    if (lock.current || !confirmed || !purchase?.canSubmit) return
    lock.current = true; setSending(true); setError('')
    try {
      const response = await fetch('/api/wb-dtu/' + token, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ values, confirmed: true }) })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Проверьте состояние заказа')
      if (mounted.current) setPurchase(previous => previous && previous.revision > body.purchase.revision ? previous : body.purchase)
    } catch (e) { if (mounted.current) setError(e.message) }
    finally { lock.current = false; if (mounted.current) setSending(false) }
  }
  const fieldChange = (key, value) => { setValues(previous => ({ ...previous, [key]: value })); setConfirmed(false) }
  const steam = !purchase?.productKind || purchase.productKind === 'steam_wallet'
  const mobile = purchase?.productKind === 'mobile'
  const productLabel = purchase?.productLabel || `${purchase?.amount} ${purchase?.currency}`
  const accountLabel = purchase?.fields?.find(field => field.key === 'account_number')?.label || 'Получатель'
  const awaitingIssue = !purchase?.completed && ((sending && purchase?.canSubmit) || ['preparing', 'pending'].includes(purchase?.state))
  return <div className='wb-dtu-page' style={{ backgroundImage: `url(${BG})` }}>
    {!purchase ? <div className='wb-dtu-loading'>{error ? <p role='alert'>{error}</p> : <IssueLoader />}</div> :
      <OrderLayout orders={purchase} kind={steam ? 'topUp' : mobile ? 'mobileTopUp' : 'DTU'} status={purchase.status} complete={Boolean(purchase.completed)}
        image={<ElectricProductImage src={purchase.image} alt={purchase.orderName} active={purchase.canSubmit && !purchase.completed && !sending} type='topup' />}
        dateLabel='Дата заказа'
        facts={[{ label: 'Сумма пополнения', value: productLabel }, { label: 'Площадка', value: 'Wildberries' }]}
        after={<>
          {!steam && <TelegramGift received={Boolean(purchase.completed)} ready={purchase.state === 'pending'} purchaseType='topup' />}
          <WbDtuHelp completed={purchase.completed} productKind={purchase.productKind} instructions={purchase.instructions} />
        </>}>
        {purchase.completed && <h2 className='order-result-title'>Пополнение выполнено</h2>}
        {purchase.canSubmit && <p className='order-note'>{steam ? 'Введите логин входа в Steam. Никнейм и ссылка на профиль не подходят. Пароль не требуется. Валюта и регион аккаунта должны соответствовать купленному товару.' : mobile ? 'Введите номер телефона с кодом страны. Проверьте оператора, страну и выбранный номинал или пакет. Пароль и код из SMS не нужны.' : 'Введите UID игрока Marvel Rivals из своего профиля в игре. Проверьте UID и выбранный номинал Lattice. Пароль не нужен.'}</p>}
        {purchase.message && <p role='status' className='order-note'>{purchase.message}</p>}
        {error && <p role='alert' className='wb-dtu-error'>{error}</p>}
        {purchase.canSubmit && !awaitingIssue ? <form className='order-form' onSubmit={submit}>
          {(purchase.fields || []).map(field => <label key={field.key}>{field.label}
            {field.type === 'list' ? <Select aria-label={field.label} value={values[field.key]} disabled={sending} options={field.options} onChange={value => fieldChange(field.key, value)} /> :
              <Input aria-label={field.label} autoComplete='off' autoCapitalize='none' spellCheck={false} required={field.required} maxLength={256} value={values[field.key] || ''} disabled={sending} onChange={event => fieldChange(field.key, event.target.value)} />}
          </label>)}
          <Checkbox checked={confirmed} disabled={sending} onChange={event => setConfirmed(event.target.checked)}>{steam ? 'Логин, регион и валюта аккаунта указаны верно.' : 'Данные получателя, страна и выбранный товар указаны верно.'} Подтверждаю: {productLabel}.</Checkbox>
          <Button htmlType='submit' type='primary' loading={sending} disabled={!confirmed || sending}>Пополнить аккаунт</Button>
        </form> : <div className='order-note'>
          {purchase.values?.account_number && <p>{accountLabel}: <strong>{purchase.values.account_number}</strong></p>}
          {awaitingIssue && <IssueLoader label='Ожидание выдачи заказа' message='Заказ обрабатывается. Статус обновится автоматически. Повторно отправлять заявку не нужно.' />}
        </div>}
        <p className='order-note wb-dtu-help'>Если нужна помощь, напишите продавцу в чате этого заказа на Wildberries.</p>
      </OrderLayout>}
    <footer className='wb-dtu-footer'>PAY2PLAY</footer>
  </div>
}
